import { ApiRequestError, recordAdEvent } from './api';
import type { AdCreative, AdEventType } from '../types';

export function adEventKey(ad: AdCreative, eventType: AdEventType): string {
  // Keep legacy non-decision surfaces independent; never merge two creative deliveries.
  return ad.decision_id ? `innova:${ad.decision_id}:${eventType}`
    : `${ad.id}-${eventType}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isTransientAdEventError(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof Error && error.name === 'TimeoutError')
    || (error instanceof ApiRequestError
    && (error.status === 408 || error.status === 429 || error.status >= 500 && error.status <= 599));
}

export interface AdEventSenderOptions { isCurrent?: () => boolean }

async function sendAttempt(
  send: typeof recordAdEvent, token: string | null, ad: AdCreative,
  eventType: AdEventType, key: string,
): Promise<void> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      const error = new Error('Ad event request timed out.');
      error.name = 'TimeoutError';
      reject(error);
      controller.abort();
    }, 5_000);
  });
  try {
    // Race also bounds an older transport that does not settle after abort.
    await Promise.race([send(token, ad, eventType, key, controller.signal), timeout]);
  } finally { clearTimeout(timer); }
}

/** Two attempts at most, 5s each, with one original key/context and no stale retry. */
export async function sendAdEventWithRetry(
  token: string | null, ad: AdCreative, eventType: AdEventType,
  send = recordAdEvent,
  wait: () => Promise<void> = () => new Promise(resolve => setTimeout(resolve, 250)),
  options: AdEventSenderOptions = {},
): Promise<void> {
  const isCurrent = options.isCurrent || (() => true);
  if (!isCurrent()) return;
  const key = adEventKey(ad, eventType);
  try { await sendAttempt(send, token, ad, eventType, key); }
  catch (error) {
    if (!isTransientAdEventError(error) || !isCurrent()) return;
    await wait();
    if (!isCurrent()) return;
    try { await sendAttempt(send, token, ad, eventType, key); }
    catch { /* Advertising telemetry never interrupts playback. */ }
  }
}
